<template>
  <div class="call-sdk-core">
    <div class="call-sdk-core__container">
      <div class="container-main">
        <CozeCore
          v-if="sdkType === 'coze'"
          ref="coreRef"
          :config="config"
          @on-end="handleCallEnd"
          @on-start="onCallStart"
        ></CozeCore>

        <AliCore
          v-else-if="sdkType === 'ali'"
          ref="coreRef"
          :config="config"
          @on-end="handleCallEnd"
          @on-start="onCallStart"
        ></AliCore>
      </div>
      <div class="container-footer">
        <div class="container-footer__button">
          <el-button circle @click="toClose">
            <i class="icon-close-line t-iconfont"></i>
          </el-button>
          <span class="container-footer__button--text">取消</span>
        </div>
        <template v-if="isCallStart">
          <div class="container-footer__button">
            <el-button circle @click="toOver">
              <i class="icon-check-line t-iconfont"></i>
            </el-button>
            <span class="container-footer__button--text">完成陪练</span>
          </div>
        </template>
      </div>
      <Dialog v-model:show="overDialogVisible" align-center title="完成本次陪练" width="400px">
        <span>完成后，AI 将基于当前的对话表现生成分析报告，确定完成吗？</span>
        <template #footer>
          <div class="dialog-footer">
            <el-button @click="overDialogVisible = false">还没完成</el-button>
            <el-button type="primary" @click="handleOverConfirm">完成陪练</el-button>
          </div>
        </template>
      </Dialog>
      <Dialog v-model:show="closeDialogVisible" align-center title="作废本次陪练" width="400px">
        <span>作废后，本次陪练对话将不进入陪练分析，确定作废吗？</span>
        <template #footer>
          <div class="dialog-footer">
            <el-button @click="closeDialogVisible = false">取消</el-button>
            <el-button type="primary" @click="handleCloseConfirm">确定</el-button>
          </div>
        </template>
      </Dialog>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, defineProps, nextTick, onMounted, onUnmounted, ref } from 'vue'
import Dialog from '@/components/ai-dialog/index.vue'

import CozeCore from '@/pages/ai-practice-partner/components/call-sdk-core/components/coze-core/index.vue'
import AliCore from '@/pages/ai-practice-partner/components/call-sdk-core/components/ali-core/index.vue'
import { startTrainTask, createTrainResult, taskHeartbeat } from '@/api/chat.js'

const emit = defineEmits(['close', 'over', 'init'])

const props = defineProps({
  params: {
    type: Object,
    default: () => ({})
  },
  isQoder: {
    type: Boolean,
    default: false
  },
  panelData: {
    type: Object,
    default: () => ({})
  }
})

const isCallStart = ref(false)
const overDialogVisible = ref(false)
const closeDialogVisible = ref(false)
const coreRef = ref(null)

const isStarted = ref(false)
const config = ref({})
const sdkType = ref('')

let intervalTask = null

const getDefaultConfig = (type = sdkType.value) => {
  if (type === 'ali') {
    return {
      agentId: '',
      agentType: 0,
      userId: '',
      region: 'cn-shanghai',
      userJoinToken: ''
    }
  }

  return {
    dataId: '',
    botId: '',
    accessToken: '',
    conversationId: '',
    promptVariable: {}
  }
}

const init = () => {
  isCallStart.value = false
  isStarted.value = false
  sdkType.value = ''
  config.value = getDefaultConfig()

  startTrainTask({
    ...props.params
  })
    .then((res) => {
      sdkType.value = Number(res.result?.enableAliTrain) === 1 ? 'ali' : 'coze'
      nextTick(() => {
        config.value = {
          ...getDefaultConfig(sdkType.value),
          ...res.result
        }
        isStarted.value = true
      })
      emit('init', config.value)
      onListenTask()
    })
    .catch((err) => {
      console.error('🚀 ~ init ~ err:', err)
      emit('close')
    })
}

const stopClient = () => {
  coreRef.value?.stopClient?.()
}

const handleCallEnd = () => {
  isCallStart.value = true
  onClearListenTask()
}

const handleCloseConfirm = () => {
  closeDialogVisible.value = false
  stopClient()
  emit('close')
}

const toClose = () => {
  closeDialogVisible.value = true
}

const formatTimeDuration = (startTime, timestamp) => {
  const seconds = Math.floor((timestamp - startTime) / 1000)
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs
    .toString()
    .padStart(2, '0')}`
}

const getSaveMessages = () => {
  const startTime = coreRef.value?.timeInfo?.startTime || 0
  const sourceMessages = coreRef.value?.messageList || []

  if (sdkType.value === 'ali') {
    return sourceMessages.map((item) => ({
      time: formatTimeDuration(startTime, item.timestamp),
      role: item.data.role,
      content: item.data.content
    }))
  }

  return sourceMessages.map((item) => {
    const dataItem = item.data.data
    return {
      time: formatTimeDuration(startTime, item.timestamp),
      role: dataItem.role === 'assistant' ? 'assistant' : 'sale',
      content: dataItem.content
    }
  })
}

const saveMessage = () => {
  const payload = {
    messages: getSaveMessages(),
    trainRecordId: config.value.dataId
  }

  if (sdkType.value === 'ali') {
    payload.instanceId = coreRef.value?.instanceId
  }

  createTrainResult(payload)
    .then(() => {})
    .finally(() => {
      emit('over')
    })
}

const handleOverConfirm = () => {
  overDialogVisible.value = false
  stopClient()
  onClearListenTask()
  saveMessage()
}

const toOver = () => {
  overDialogVisible.value = true
}

const onListenTask = () => {
  const timerS = 2 * 60 * 1000
  intervalTask = setInterval(() => {
    taskHeartbeat({
      analystTrainRecordId: config.value.dataId
    })
  }, timerS)
}

const onClearListenTask = () => {
  clearInterval(intervalTask)
  intervalTask = null
}

const onCallStart = () => {
  setTimeout(() => {
    isCallStart.value = true
  }, 1000)
}

onMounted(() => {
  init()
})

onUnmounted(() => {
  onClearListenTask()
})
</script>

<style lang="less" scoped>
.call-sdk-core {
  width: 100%;
  height: 100%;

  &__container {
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    position: relative;
    display: flex;
    flex-direction: column;
    align-content: center;

    .container-main {
      flex-grow: 1;
      min-height: 0;
    }

    .container-footer {
      padding: 24px 0;
      flex-shrink: 0;
      display: flex;
      justify-content: center;

      &__button {
        display: flex;
        flex-direction: column;
        align-items: center;

        & + .container-footer__button {
          margin-left: 24px;
        }

        .el-button {
          width: 56px;
          height: 56px;
          background: rgba(201, 205, 212, 0.2);
          border: none;
          margin: 0;

          .t-iconfont {
            width: 24px;
            height: 24px;
            font-size: 24px;
            color: #323233;
          }

          .icon-check-line {
            background: linear-gradient(286deg, #1574ff 2%, #5952ff 51%, #a969fe 96%);
            background-clip: text;
            color: transparent;
            -webkit-text-fill-color: transparent;
          }
        }

        &--text {
          margin-top: 4px;
          color: #969799;
          line-height: 16px;
          font-size: 12px;
        }
      }
    }
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;

  .el-button + .el-button {
    margin-left: 12px;
  }
}
</style>
